import clsx from 'clsx';
import React, { type PropsWithChildren, useCallback } from 'react';

type Option<T extends string> = { label: string; value: T };

type TabV2Props<T extends string> = Pick<Option<T>, 'value'> & {
  selected?: boolean;
  onClick: (value: T) => void;
};

const TabV2 = <T extends string>({
  children,
  value,
  onClick,
  selected = false,
}: PropsWithChildren<TabV2Props<T>>) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    className={clsx(
      'flex items-center',
      'px-f1 py-f1',
      'text-xmd',
      selected
        ? 'bg-transparent text-main'
        : 'bg-transparent text-main-500 border-b-[0.5px] border-main-200',
      'hover:text-main hover:bg-main-100 hover:cursor-pointer',
      'last:border-r-[0.5px] border-l-[0.5px] first:border-l-0 border-main-200',
      'transition-colors',
      'box-border'
    )}
    data-testid={value}
  >
    {children}
  </button>
);

type SortProps = {
  label: string;
  order: 'asc' | 'desc';
  onToggle: () => void;
};

type TabsV2Props<T extends string> = {
  options: Option<T>[] | readonly Option<T>[];
  selectedValue?: T;
  onClick: (val: T) => void;
  extraClassNames?: string;
  'data-testid'?: string;
  sort?: SortProps;
};

export const TabsV2 = <T extends string>({
  options,
  'data-testid': dataTestId,
  selectedValue = undefined,
  extraClassNames,
  onClick,
  sort,
}: TabsV2Props<T>) => {
  const onClickHandler = useCallback(
    (value: T) => {
      onClick(value);
    },
    [onClick]
  );

  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        'flex items-end box-border',
        sort ? 'w-full' : 'w-fit overflow-auto scrollbar-hide truncate',
        extraClassNames
      )}
    >
      <div className="flex">
        {options.map((opt) => (
          <TabV2
            key={opt.value}
            value={opt.value}
            onClick={onClickHandler}
            selected={opt.value === selectedValue}
          >
            {opt.label}
          </TabV2>
        ))}
      </div>
      {sort && (
        <>
          <div className="flex-1 border-b-[0.5px] border-main-200" />
          <button
            type="button"
            onClick={sort.onToggle}
            className="flex items-center gap-x-0.5 px-f1 py-f1 text-xmd text-main-500 hover:text-main border-b-[0.5px] border-main-200"
          >
            {sort.label}
            <span>{sort.order === 'desc' ? '↓' : '↑'}</span>
          </button>
        </>
      )}
    </div>
  );
};
